// client\src\components\ListaFormularios.jsx

import { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import styles from "../App.module.css";
import {
  FaExternalLinkAlt,
  FaRegFileAlt,
  FaHourglassHalf,
  FaFileSignature,
  FaRegCheckCircle,
  FaPen
} from "react-icons/fa";
import ReactCountryFlag from "react-country-flag";

// === MAPEIA TIPO -> PAÍS (MESMO PADRÃO DO SimpleFormsPage) ===
const countryByTipo = (tipo = "") => {
  switch (tipo.toLowerCase()) {
    case "adulto":
    case "kid":
    case "i140":
      return "US"; // EUA
    case "canada":
      return "CA"; // Canadá
    case "passport":
      return "BR"; // Brasil
    case "australia":
      return "AU"; // Austrália
    default:
      return null;
  }
};

// Componente para exibir bandeira + tipo
const TipoWithFlag = ({ tipo }) => {
  const code = countryByTipo(tipo || "");
  const label = (tipo || "").toUpperCase();

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      {code && (
        <ReactCountryFlag
          countryCode={code}
          svg
          style={{ width: 24, height: 18, display: "inline-block" }}
          aria-label={code}
        />
      )}
      <strong>{label || "SEM TIPO"}</strong>
    </span>
  );
};

// Ícone de status (mesmo conceito do SimpleFormsPage)
const getStatusIcon = (status) => {
  switch (status) {
    case "pendente":
      return <FaHourglassHalf size={20} color="gray" />;
    case "preenchendo":
      return <FaFileSignature size={20} color="orange" />;
    case "completo":
      return <FaRegCheckCircle size={20} color="green" />;
    default:
      return null;
  }
};

// Função auxiliar para acessar campos aninhados
const getNestedField = (data, fieldPath) => {
  const fields = fieldPath.split(".");
  return fields.reduce((acc, field) => {
    if (acc === null || acc === undefined) return null;
    return acc[field] !== undefined ? acc[field] : null;
  }, data);
};

// STEPS por tipo (baseado no SimpleFormsPage)
const STEPS_ADULTO = [
  "Dados Pessoais",
  "Informações de Viagem",
  "Informações de Contatos nos EUA",
  "Endereço e Contato",
  "Dados de Passaporte",
  "Informações de Família",
  "Ocupação Atual",
  "Formações Acadêmicas",
  "Informações Adicionais",
];
const STEPS_KID = [
  "Dados Pessoais",
  "Informações de Viagem",
  "Residência e Vistos nos EUA",
  "Endereço e Contato",
  "Dados de Passaporte",
  "Informações de Família",
];
const STEPS_PASSPORT = ["Dados Pessoais", "Endereço e Contato", "Dados do Passaporte"];
const STEPS_CANADA = [
  "Dados Pessoais",
  "Endereço e Contato",
  "Dados de Passaporte",
  "Informações de Família",
  "Informacoes De Viagem",
  "Vistos E Permanências",
  "Ocupação Atual",
  "Ocupação Anterior",
  "Documentos Adicionais",
  "Informações adcionais",
];
const STEPS_AUSTRALIA = [
  "Dados Pessoais",
  "Endereço e Contato",
  "Dados do Passaporte",
  "Informações Adicionais",
];
const STEPS_I140 = [
  "Dados Pessoais",
  "Endereço e Contato",
  "Dados do Passaporte",
  "Informações Adicionais",
];

const getStepsByType = (tipo) => {
  if (tipo === "kid") return STEPS_KID;
  if (tipo === "passport") return STEPS_PASSPORT;
  if (tipo === "canada") return STEPS_CANADA;
  if (tipo === "australia") return STEPS_AUSTRALIA;
  if (tipo === "i140") return STEPS_I140;
  return STEPS_ADULTO; // default adulto
};

// Renderiza quadradinhos de steps coloridos (similar ao SimpleFormsPage)
const renderColoredSteps = (formData, tipo, formStatus) => {
  // Se estiver pendente, não mostra steps (igual comportamento da outra página)
  if (formStatus === "pendente") return null;

  const requiredFields = {
    passport: {
      0: ["nome", "cpf", "dataNascimento"],
      1: ["endereco", "email", "telefone", "cep"],
      2: ["jaTevePassaporte", "formaPagamento", "postoPF"],
    },
    adulto: {
      0: ["nome", "sobrenome", "cidadeNascimento", "dataNascimento", "cpf"],
      1: ["dataChegada", "duracaoViagem", "cidadePretendida", "hospedagem"],
      2: ["possuiSocialSecurity", "possuiIdFiscalEUA", "esteveEUA"],
      3: ["endereco", "bairro", "cep", "cidadeEstado"],
      4: ["dataEmissaoPassaporte", "dataExpiracaoPassaporte", "numeroPassaporte"],
      5: ["nomePai", "dataNascimentoPai", "nomeMae", "dataNascimentoMae"],
      6: ["ocupacaoAtual", "empresa", "rendaMensal"],
      7: ["ocupacaoAnterior", "formacaoAcademica"],
      8: ["participaOrganizacoes", "temHabilidadesEspeciais", "temCondenacoes"],
    },
    kid: {
      0: ["nome", "sobrenome", "cidadeNascimento", "dataNascimento", "cpf"],
      1: ["dataChegada", "duracaoViagem", "cidadePretendida", "hospedagem"],
      2: ["possuiSocialSecurity", "possuiIdFiscalEUA", "esteveEUA"],
      3: ["endereco", "bairro", "cep", "cidadeEstado"],
      4: ["dataEmissaoPassaporte", "dataExpiracaoPassaporte", "numeroPassaporte"],
      5: ["nomePai", "dataNascimentoPai", "nomeMae", "dataNascimentoMae"],
      6: ["ocupacaoAtual", "empresa", "rendaMensal"],
      7: ["ocupacaoAnterior", "formacaoAcademica"],
      8: ["participaOrganizacoes", "temHabilidadesEspeciais", "temCondenacoes"],
    },
    canada: {
      0: ["nome", "sobrenome", "cidadeNascimento", "dataNascimento", "cpf"],
      1: ["endereco", "cep", "cidadeEstado"],
      2: [
        "dataEmissaoPassaporte",
        "dataExpiracaoPassaporte",
        "numeroPassaporte",
        "cidadeEstadoEmissaoPassaporte",
        "numeroIdentidade",
        "dataEmissaoIdentidade",
        "cidadeEstadoEmissaoIdentidade",
      ],
      3: [],
      4: ["motivoViagemCanada", "tempoPermanenciaCanada", "dataPretendidaViagem", "cidadePretendidaCanada"],
      5: [],
      6: ["ocupacaoAtual", "dataInicio"],
      7: [],
      8: [],
      9: [],
    },
  };

  const activeSteps = getStepsByType(tipo);
  const fieldsByStep = requiredFields[tipo] || requiredFields["adulto"] || {};

  const getStepStatusColor = (stepIndex) => {
    const stepFields = fieldsByStep[stepIndex];
    if (!stepFields) return "#646464"; // passo indefinido = cinza

    const isComplete = stepFields.every((field) => {
      const value = getNestedField(formData, field);
      return Array.isArray(value) ? value.length > 0 : value && value.toString().trim() !== "";
    });

    const isPartial = stepFields.some((field) => {
      const value = getNestedField(formData, field);
      return Array.isArray(value) ? value.length > 0 : value && value.toString().trim() !== "";
    });

    return isComplete ? "#479c00" : isPartial ? "rgb(251 141 20)" : "#646464";
  };

  let lastStepAnswered = 0;
  for (let i = 0; i < activeSteps.length; i++) {
    const stepFields = fieldsByStep[i];
    if (!stepFields) continue;

    const answered = stepFields.some((field) => {
      const value = getNestedField(formData, field);
      return Array.isArray(value) ? value.length > 0 : value && value.toString().trim() !== "";
    });

    if (answered) {
      lastStepAnswered = i + 1;
    }
  }
  if (lastStepAnswered === 0) lastStepAnswered = 1;
  const stepsToShow = activeSteps.slice(0, lastStepAnswered);

  return (
    <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "8px" }}>
      {stepsToShow.map((_, index) => (
        <div
          key={index}
          style={{
            width: "26px",
            height: "26px",
            backgroundColor: getStepStatusColor(index),
            borderRadius: "4px",
            textAlign: "center",
            color: "white",
            lineHeight: "26px",
            fontSize: "0.8rem",
          }}
        >
          {index + 1}
        </div>
      ))}
    </div>
  );
};

const ListaFormularios = ({
  users,
  groupColors = ["#8BAED3", "#F29098", "#808EA3", "#C0ECAE"],
  onClickVer = () => { },
  onClickFinalizar = () => { },
  onClickAguardar = () => { },
  onClickAnalise = () => { },
  setCurrentFormContent = () => { },
  setCurrentFormId = () => { },
  setIsModalOpenForm = () => { },
  tipo = "pendencias", // ou "producao" (só para URL de ver)
}) => {
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [usersOptions, setUsersOptions] = useState([]); // vendedores/produtores disponíveis
  const [editData, setEditData] = useState({
    id: null,
    vendedor: "",
    produtor: "",
    colaboradores: [],
    obs: "",
    aguardando: "",
    dsAdulto: null,
    dsKids: null,
    I140Form: null,
    australiaForm: null,
    passport: null,
    canadaForm: null,
  });

  const parseColabs = (colabRaw) => {
    try {
      if (!colabRaw) return [];
      if (Array.isArray(colabRaw)) return colabRaw;
      return JSON.parse(colabRaw);
    } catch {
      return [];
    }
  };

  const openEdit = (user) => {
    setEditData({
      id: user.produzirId,
      vendedor: user.vendedor || "",
      produtor: user.produtor || "",
      colaboradores: parseColabs(user.colaboradores),
      obs: user.obs || "",
      aguardando: user.aguardando || "",
      dsAdulto: user.dsAdulto ?? null,
      dsKids: user.dsKids ?? null,
      canadaForm: user.canadaForm ?? null,
      I140Form: user.I140Form ?? null,
      australiaForm: user.australiaForm ?? null,
      passport: user.passport ?? null,
    });
    setEditOpen(true);
  };

  useEffect(() => {
    if (!editOpen) return;
    (async () => {
      try {
        const res = await api.get("/vendedores-produtores");
        const list = (res.data?.data || []).map((u) => ({
          nome: u.nome,
          cpf: String(u.cpf || "").replace(/\D/g, ""),
          is_vendedor: u.is_vendedor === 1,
          is_produtor: u.is_produtor === 1,
        }));
        const uniq = [];
        const seen = new Set();
        for (const u of list) {
          if (u.cpf && !seen.has(u.cpf)) {
            seen.add(u.cpf);
            uniq.push(u);
          }
        }
        setUsersOptions(uniq);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [editOpen]);

  const availableColabOptions = useMemo(() => {
    const vend = String(editData.vendedor || "");
    const prod = String(editData.produtor || "");
    return usersOptions.filter((u) => u.cpf !== vend && u.cpf !== prod);
  }, [usersOptions, editData.vendedor, editData.produtor]);

  useEffect(() => {
    setEditData((prev) => ({
      ...prev,
      colaboradores: (prev.colaboradores || []).filter(
        (c) => c !== String(prev.vendedor || "") && c !== String(prev.produtor || "")
      ),
    }));
    // eslint-disable-next-line
  }, [editData.vendedor, editData.produtor]);

  const toggleColab = (cpf) => {
    cpf = String(cpf).replace(/\D/g, "");
    setEditData((prev) => {
      const inside = (prev.colaboradores || []).includes(cpf);
      return {
        ...prev,
        colaboradores: inside
          ? prev.colaboradores.filter((c) => c !== cpf)
          : [...prev.colaboradores, cpf],
      };
    });
  };

      const appOrigin = (typeof window !== "undefined" && window.location?.appOrigin) ? window.location.appOrigin : "";


  return (
    <>
      {users.map((user, index) => {
        const backgroundColor = groupColors[index % groupColors.length];
        const todosCompletos = user.formularios.every((f) => f.status === "completo");

        return (
          <div key={user.produzirId} style={{ width: "100%", position: "relative" }}>
            {/* Cabeçalho usuário */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-evenly",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-evenly",
                  minWidth: "30%",
                  backgroundColor: "var(--corPrimaria)",
                  borderTopRightRadius: "30px",
                  borderTopLeftRadius: "30px",
                  paddingTop: "10px",
                  alignItems: "center",
                }}
              >
                <h2 className={styles.groupTitle}>{user.login || "Email não cadastrado"}</h2>
              </div>
            </div>

            {/* Lista de formulários */}
            <div className={styles.groupContainer} style={{ backgroundColor, width: "100%" }}>
              <div className={styles.formTable}>
                {user.formularios.map((formulario) => {
                  let formularioData = {};
                  try {
                    formularioData = JSON.parse(formulario.formularioData || "{}");
                  } catch {
                    formularioData = {};
                  }

                  const nomeCompleto = `${formularioData.nome || ""} ${formularioData.sobrenome || ""
                    }`.trim();
                  const anoNascimento = formularioData.dataNascimento
                    ? new Date(formularioData.dataNascimento).getFullYear()
                    : "Não informado";

                  const tipoForm = formulario.tipo || "adulto";
                  const statusForm = formulario.status || "pendente";

                  return (


                    <>



                      <div>
                        {/* NOVO BLOCO: Tipo + Bandeira + Status + Ícone */}
                        <div
                          style={{
                            marginTop: "8px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "10px",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <TipoWithFlag tipo={tipoForm} />{" "}  {(formulario.ds160_id) || ""}
                          </div>
                        </div>
                        {/* NOVO BLOCO: Steps coloridos */}
                        {renderColoredSteps(formularioData, tipoForm, statusForm)}
                      </div>


                      <div
                        key={formulario.id}
                        className={styles.row}
                        style={{ position: "relative", padding: "15px" }}
                      >
                        {/* Overlay individual se apenas esse formulário estiver completo */}
                        {!todosCompletos && statusForm === "completo" && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              backgroundColor: "rgba(0, 0, 0, 0.65)",
                              color: "white",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              zIndex: 5,
                              pointerEvents: "none",
                            }}
                          >
                            <span style={{ fontSize: "1rem", fontWeight: "bold" }}>
                              Formulário encerrado
                            </span>
                          </div>
                        )}

                        <div className={styles.column2}>{nomeCompleto || "Não informado"}</div>
                        <div className={styles.column2}>{anoNascimento}</div>
                        {/* Botão VER FORMULÁRIO (substitui a segunda linha duplicada de anoNascimento) */}
                        <div className={styles.column2}>
                          <a href={`${appOrigin}/preencher/${formulario.id}`}>
                            <div  style={{ backgroundColor: "green", color: 'white', padding:"10px", borderRadius:"10px", }}> Ver </div>
                          </a>
                        </div>
                      </div>
                    </>
                  );
                })}
              </div>
            </div>

            {/* Observações e Motivo Aguardando */}
            {user.obs && (
              <div
                style={{
                  width: "100%",
                  minHeight: "30px",
                  maxHeight: "400px",
                  overflow: "auto",
                  resize: "none",
                }}
              >
                <p
                  style={{
                    padding: "5px",
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#e0e0e0",
                    fontSize: "1rem",
                  }}
                >
                  <b>OBS:</b> {user.obs}
                </p>
              </div>
            )}
            {user.aguardando && (
              <div
                style={{
                  width: "100%",
                  minHeight: "30px",
                  maxHeight: "400px",
                  overflow: "auto",
                  resize: "none",
                }}
              >
                <p
                  style={{
                    padding: "5px",
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#FFB628",
                    color: "var(--corPrimaria)",
                    fontSize: "1rem",
                  }}
                >
                  <b>Motivo (Aguardar):</b> {user.aguardando}
                </p>
              </div>
            )}
            {(() => {
              let colaboradores = [];
              if (Array.isArray(user.colaboradores)) {
                colaboradores = user.colaboradores;
              } else {
                try {
                  colaboradores = JSON.parse(user.colaboradores || "[]");
                } catch {
                  colaboradores = [];
                }
              }
              return colaboradores.length > 0 ? (
                <div
                  style={{
                    width: "100%",
                    minHeight: 30,
                    maxHeight: 400,
                    overflow: "auto",
                  }}
                >
                  <p
                    style={{
                      padding: 5,
                      border: "1px solid #e0e0e0",
                      background: "#f6f6f6",
                      fontSize: "1rem",
                    }}
                  >
                    <b>Colaboradores:</b> {colaboradores.join(", ")}
                  </p>
                </div>
              ) : null;
            })()}
            {todosCompletos && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "20px",
                  zIndex: 5,
                  pointerEvents: "none",
                  flexDirection: "column",
                  textAlign: "center",
                }}
              >
                <h2 style={{ fontSize: "2rem", fontWeight: "bold" }}>ENCERRADO</h2>
                <p style={{ fontSize: "1rem", marginTop: "10px" }}>
                  Todos os formulários deste cliente foram finalizados
                </p>
              </div>
            )}

            {/* Botões de ação */}
            <div
              style={{
                width: "100%",
                display: "flex",
                alignContent: "center",
                justifyContent: "space-around",
                paddingBlock: "10px",
              }}
            >
              <button
                onClick={() => onClickVer(user)}
                style={{
                  padding: "5px 5px",
                  backgroundColor: "#007BFF00",
                  color: "var(--corPrimaria)",
                  borderRadius: "5px",
                  border: `solid 1px var(--corPrimaria)`,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
                title="Ir para a página de preenchimento"
              >
                <FaExternalLinkAlt /> Acessar Grupo
              </button>

              {user.analiseProd && (
                <button
                  onClick={() => onClickAnalise(user)}
                  style={{
                    padding: "5px 5px",
                    backgroundColor: "var(--corPrimaria)",
                    color: "white",
                    borderRadius: "5px",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                  title="Análise de perfil"
                >
                  <FaRegFileAlt /> Análise
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* MODAL DE EDIÇÃO (sem alterações) */}
      {editOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 16,
              width: "min(900px, 90vw)",
              maxWidth: "900px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <h3 style={{ margin: 0 }}>Editar demanda #{editData.id}</h3>
              <button
                onClick={() => setEditOpen(false)}
                style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer" }}
              >
                ×
              </button>
            </div>

            {/* Vendedor / Produtor */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div>
                <label className={styles.inputText}>Vendedor responsável</label>
                <select
                  value={editData.vendedor}
                  onChange={(e) => setEditData((d) => ({ ...d, vendedor: e.target.value }))}
                  className={styles.inputStyleVendedor}
                >
                  <option value="">Selecione</option>
                  {usersOptions
                    .filter((u) => u.is_vendedor)
                    .map((u) => (
                      <option key={u.cpf} value={u.cpf}>
                        {u.nome} - {u.cpf}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className={styles.inputText}>Produtor</label>
                <select
                  value={editData.produtor}
                  onChange={(e) => setEditData((d) => ({ ...d, produtor: e.target.value }))}
                  className={styles.inputStyleVendedor}
                >
                  <option value="">Selecione</option>
                  {usersOptions
                    .filter((u) => u.is_produtor)
                    .map((u) => (
                      <option key={u.cpf} value={u.cpf}>
                        {u.nome} - {u.cpf}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* Colaboradores (botões) */}
            <div style={{ marginBottom: 12 }}>
              <label className={styles.inputText}>Colaboradores</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                {availableColabOptions.map((u) => {
                  const selected = (editData.colaboradores || []).includes(u.cpf);
                  return (
                    <button
                      key={u.cpf}
                      type="button"
                      onClick={() => toggleColab(u.cpf)}
                      aria-pressed={selected}
                      className={
                        styles.colabButton +
                        " " +
                        (selected ? styles.colabButtonSelected : "")
                      }
                      style={{
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: "1px solid var(--linha, #ddd)",
                        background: selected ? "var(--corPrimaria)" : "transparent",
                        color: selected ? "var(--onPrimary,#fff)" : "inherit",
                        cursor: "pointer",
                        transition:
                          "background .15s,color .15s,border-color .15s",
                      }}
                    >
                      {u.nome} - {u.cpf}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* OBS e Aguardando */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div className={styles.fieldStack}>
                <label className={styles.inputText}>Observações</label>
                <textarea
                  className={`${styles.growTextarea}`}
                  value={editData.obs}
                  onChange={(e) =>
                    setEditData((d) => ({ ...d, obs: e.target.value }))
                  }
                  placeholder="Obs da demanda"
                />
              </div>
              <div className={styles.fieldStack}>
                <label className={styles.inputText}>Aguardando (motivo)</label>
                <textarea
                  className={`${styles.growTextarea}`}
                  value={editData.aguardando}
                  onChange={(e) =>
                    setEditData((d) => ({ ...d, aguardando: e.target.value }))
                  }
                  placeholder="Motivo do Aguardar"
                />
              </div>
            </div>

            {/* Demandas (opcional) */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div>
                <label className={styles.inputText}>DS-160 Adulto</label>
                <input
                  className={styles.inputStyleMin}
                  type="number"
                  min="0"
                  value={editData.dsAdulto ?? ""}
                  onChange={(e) =>
                    setEditData((d) => ({
                      ...d,
                      dsAdulto: e.target.value === "" ? null : Number(e.target.value),
                    }))
                  }
                />
              </div>
              <div>
                <label className={styles.inputText}>DS-160 Criança</label>
                <input
                  className={styles.inputStyleMin}
                  type="number"
                  min="0"
                  value={editData.dsKids ?? ""}
                  onChange={(e) =>
                    setEditData((d) => ({
                      ...d,
                      dsKids: e.target.value === "" ? null : Number(e.target.value),
                    }))
                  }
                />
              </div>
              <div>
                <label className={styles.inputText}>canadaForm</label>
                <input
                  className={styles.inputStyleMin}
                  type="number"
                  min="0"
                  value={editData.canadaForm ?? ""}
                  onChange={(e) =>
                    setEditData((d) => ({
                      ...d,
                      canadaForm: e.target.value === "" ? null : Number(e.target.value),
                    }))
                  }
                />
              </div>

              <div>
                <label className={styles.inputText}>australiaForm</label>
                <input
                  className={styles.inputStyleMin}
                  type="number"
                  min="0"
                  value={editData.australiaForm ?? ""}
                  onChange={(e) =>
                    setEditData((d) => ({
                      ...d,
                      australiaForm:
                        e.target.value === "" ? null : Number(e.target.value),
                    }))
                  }
                />
              </div>
              <div>
                <label className={styles.inputText}>I140Form</label>
                <input
                  className={styles.inputStyleMin}
                  type="number"
                  min="0"
                  value={editData.I140Form ?? ""}
                  onChange={(e) =>
                    setEditData((d) => ({
                      ...d,
                      I140Form: e.target.value === "" ? null : Number(e.target.value),
                    }))
                  }
                />
              </div>
              <div>
                <label className={styles.inputText}>Passaportes</label>
                <input
                  className={styles.inputStyleMin}
                  type="number"
                  min="0"
                  value={editData.passport ?? ""}
                  onChange={(e) =>
                    setEditData((d) => ({
                      ...d,
                      passport: e.target.value === "" ? null : Number(e.target.value),
                    }))
                  }
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <button
                onClick={() => setEditOpen(false)}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  background: "#f5f5f5",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  try {
                    setEditLoading(true);
                    const payload = {
                      id: editData.id,
                      vendedor: editData.vendedor || null,
                      produtor: editData.produtor || null,
                      colaboradores: editData.colaboradores || null,
                      obsProd: editData.obs ?? null,
                      aguardando: editData.aguardando ?? null,
                      dsAdulto: editData.dsAdulto,
                      dsKids: editData.dsKids,
                      canadaForm: editData.canadaForm,
                      I140Form: editData.I140Form,
                      australiaForm: editData.australiaForm,
                      passport: editData.passport,
                    };
                    const res = await api.post("/updateUserProd", payload);
                    if (res.data?.status) {
                      setEditOpen(false);
                      window.location.reload(); // fallback simples
                    } else {
                      alert("Falha ao salvar.");
                    }
                  } catch (e) {
                    console.error(e);
                    alert("Erro ao salvar.");
                  } finally {
                    setEditLoading(false);
                  }
                }}
                disabled={editLoading}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "none",
                  background: "var(--corPrimaria)",
                  color: "#fff",
                }}
              >
                {editLoading ? "Salvando..." : "Salvar alterações"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListaFormularios;
